<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RenameDialog from '@/components/tagSettings/RenameDialog.vue'
import { useTagEditor } from '@/composables/useTagEditor'
import { tagsPath } from '@/settings/tagsNav'
import { chipStyle } from '@/todos/tags'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

// One tag: its name (tap to edit, enter accepts and asks what to do with the todos, escape cancels, leaving the field keeps
// the typed text until the row is edited again), its color and the sub pages. Deleting is the last row.
const props = defineProps<{ tagId: number }>()
const { t } = useI18n()
const router = useRouter()
const editor = useTagEditor()
const { drafts } = editor

const tag = computed(() => editor.tags.value.find((existing) => existing.id === props.tagId))
const editing = ref(false)
const input = ref<HTMLInputElement | null>(null)

async function startEdit(): Promise<void> {
  if (!tag.value) return
  editing.value = true
  drafts[tag.value.id] ??= tag.value.name
  await nextTick()
  input.value?.focus()
}

function accept(): void {
  if (!tag.value) return
  editor.askRename(tag.value)
  if (drafts[tag.value.id] === undefined) editing.value = false
}

function cancel(): void {
  if (tag.value) editor.cancel(tag.value.id)
  editing.value = false
}
</script>

<template>
  <div v-if="tag" class="tag-detail" :data-tag-id="tag.id">
    <UiGroup>
      <UiList>
        <UiRow :label="t('settings.tags.name')">
          <template v-if="editing">
            <input
              ref="input"
              v-model="drafts[tag.id]"
              class="ui-input name-input"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              :aria-label="t('settings.tags.name')"
              @keydown.enter.prevent="accept"
              @keydown.esc.prevent="cancel"
              @blur="editing = false"
            />
            <button
              type="button"
              class="icon"
              :title="t('common.save')"
              @mousedown.prevent
              @click="accept"
            >
              ✓
            </button>
            <button
              type="button"
              class="icon"
              :title="t('common.cancel')"
              @mousedown.prevent
              @click="cancel"
            >
              ✕
            </button>
          </template>
          <template v-else>
            <button type="button" class="name" @click="startEdit">
              <span class="label" :style="chipStyle(tag.color)"># {{ tag.name }}</span>
              <span v-if="drafts[tag.id] !== undefined" class="ui-hint pending">{{
                t('settings.tags.unsaved')
              }}</span>
            </button>
          </template>
        </UiRow>
        <UiRow :label="t('settings.tags.color')">
          <span class="color-value">
            <input
              type="color"
              class="color"
              :class="{ unset: !tag.color }"
              :value="tag.color ?? '#808080'"
              :aria-label="t('settings.tags.color')"
              @change="editor.setColor(tag, ($event.target as HTMLInputElement).value)"
            />
            <button v-if="tag.color" type="button" class="link" @click="editor.setColor(tag, null)">
              {{ t('settings.tags.clearColor') }}
            </button>
          </span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow :to="tagsPath(tag.id, 'aliases')" class="entry">
          <span class="entry-title">{{ t('settings.tags.aliases') }}</span>
          <span v-if="tag.aliases.length" class="ui-hint">{{ tag.aliases.length }}</span>
          <span class="go">›</span>
        </UiRow>
        <UiRow v-if="!tag.internal" :to="tagsPath(tag.id, 'implied')" class="entry">
          <span class="entry-title">{{ t('settings.tags.implies') }}</span>
          <span v-if="tag.implies.length" class="ui-hint">{{ tag.implies.length }}</span>
          <span class="go">›</span>
        </UiRow>
        <UiRow :to="tagsPath(tag.id, 'arrangement')" class="entry">
          <span class="entry-title">{{ t('settings.tags.arrangement') }}</span>
          <span class="go">›</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <p v-if="tag.internal" class="ui-hint">{{ t('settings.tags.builtIn') }}</p>
    <UiGroup v-else>
      <UiList>
        <UiRow
          as="button"
          type="button"
          class="delete"
          @click="editor.remove(tag, () => router.replace(tagsPath()))"
        >
          {{ t('settings.tags.delete') }}
        </UiRow>
      </UiList>
    </UiGroup>

    <RenameDialog />
  </div>
  <p v-else-if="editor.loaded.value" class="ui-hint">
    {{ t('settings.tags.gone') }}
    <RouterLink :to="tagsPath()">{{ t('settings.tags.title') }}</RouterLink>
  </p>
  <p v-else>{{ t('common.loading') }}</p>
</template>

<style scoped>
.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.entry-title {
  flex: 1;
  min-width: 0;
}

.go {
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}

.name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.label {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  overflow-wrap: anywhere;
}

.name-input {
  flex: 1;
  min-width: 0;
}

.color-value {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.color {
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  border: none;
  background: none;
}

.color.unset {
  opacity: 0.35;
}

.link {
  width: auto;
  padding: 0;
  background: none;
  color: var(--tg-link-color);
  border: none;
  font-size: 0.85rem;
}

.icon {
  width: auto;
  padding: 0.1rem 0.4rem;
  background: none;
  color: inherit;
  border: none;
  cursor: pointer;
}

.delete {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-destructive-text-color);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.pending {
  font-style: italic;
}
</style>
